/**
 * 🏛️ WORLD WONDERS - Legendary structures across the ages
 * 
 * Rules:
 * - Max 3 wonders total (ever)
 * - Only 1 under construction at a time
 * - Building costs consume resources AND buildings
 * - Long build times (10-45 minutes based on tier)
 * - Powerful passive effects
 * 
 * FIXED: All building requirements now reference valid building IDs
 */

export const WONDERS = {
  // ===========================================
  // TIER 1 - PRIMITIVE AGE (10 min build time)
  // ===========================================
  stonehenge: {
    id: 'stonehenge',
    name: 'Stonehenge',
    icon: '🪨',
    tier: 1,
    description: 'Ancient stone circle aligned with the heavens. Grants mystical insight.',
    flavorText: '"The stones remember what we have forgotten."',
    buildTime: 600,
    cost: {
      resources: { stone: 500, wood: 200 },
      buildings: { quarry: 2 },
    },
    effects: {
      productionBonus: { knowledge: 0.25 },
      flatBonus: { knowledge: 0.5 },
    },
    lore: 'Erected by unknown hands in ages past, these standing stones channel celestial energies.',
  },

  great_bonfire: {
    id: 'great_bonfire',
    name: 'The Eternal Flame',
    icon: '🔥',
    tier: 1,
    description: 'A massive bonfire that never dies. Draws settlers from afar.',
    flavorText: '"Where there is fire, there is hope."',
    buildTime: 600,
    cost: {
      resources: { wood: 800, food: 300 },
      buildings: { woodcutter: 3 },
    },
    effects: {
      productionBonus: { food: 0.20 },
      flatBonus: { population: 0.3 },
    },
    lore: 'The flame has burned for generations, a beacon of warmth in a cold world.',
  },

  tribal_monument: {
    id: 'tribal_monument',
    name: 'Ancestral Totem',
    icon: '🗿',
    tier: 1,
    description: 'A towering monument to honor the ancestors. Inspires great works.',
    flavorText: '"We stand upon the shoulders of those who came before."',
    buildTime: 600,
    cost: {
      resources: { wood: 600, stone: 600 },
      buildings: { woodcutter: 3, quarry: 2 },
    },
    effects: {
      productionBonus: { wood: 0.20, stone: 0.20 },
      globalProductionBonus: 0.03,
    },
    lore: 'Carved with the deeds of ancestors, it reminds all of their heritage.',
  },

  // ===========================================
  // TIER 2 - STONE AGE (12 min build time)
  // ===========================================
  great_granary: {
    id: 'great_granary',
    name: 'The Great Granary',
    icon: '🌾',
    tier: 2,
    description: 'Massive food stores that can feed thousands. Prevents famine.',
    flavorText: '"A full belly makes a content people."',
    buildTime: 720,
    cost: {
      resources: { stone: 1000, wood: 600, food: 500 },
      buildings: { farm: 3 },
    },
    effects: {
      productionBonus: { food: 0.40 },
      flatBonus: { food: 1 },
    },
    lore: 'Built to withstand siege and famine alike, it holds grain for seven years.',
  },

  stone_citadel: {
    id: 'stone_citadel',
    name: 'Stone Citadel',
    icon: '🏰',
    tier: 2,
    description: 'An impenetrable fortress. Symbol of strength and security.',
    flavorText: '"These walls have never fallen."',
    buildTime: 720,
    cost: {
      resources: { stone: 1500, wood: 400 },
      buildings: { quarry: 4 },
    },
    effects: {
      productionBonus: { stone: 0.35 },
      flatBonus: { population: 0.5 },
    },
    lore: 'Its walls reach to the clouds, a testament to human determination.',
  },

  oracle_temple: {
    id: 'oracle_temple',
    name: 'Temple of the Oracle',
    icon: '🏛️',
    tier: 2,
    description: 'Priests commune with the divine, revealing hidden truths.',
    flavorText: '"The Oracle speaks, and kings listen."',
    buildTime: 720,
    cost: {
      resources: { stone: 800, gold: 200, knowledge: 100 },
      buildings: { library: 1 },
    },
    effects: {
      productionBonus: { knowledge: 0.35 },
      researchSpeedBonus: 0.08,
    },
    lore: 'Pilgrims travel from distant lands to hear the Oracle\'s prophecies.',
  },

  // ===========================================
  // TIER 3 - AGRICULTURAL AGE (15 min build time)
  // ===========================================
  hanging_gardens: {
    id: 'hanging_gardens',
    name: 'Hanging Gardens',
    icon: '🌺',
    tier: 3,
    description: 'Terraced gardens that defy nature. A paradise on earth.',
    flavorText: '"In the desert, we created paradise."',
    buildTime: 900,
    cost: {
      resources: { stone: 2000, wood: 1000, gold: 500, food: 1000 },
      buildings: { farm: 5 },
    },
    effects: {
      productionBonus: { food: 0.55, culture: 0.45 },
      flatBonus: { population: 1 },
    },
    lore: 'Water cascades through impossible terraces, feeding exotic plants from every land.',
  },

  great_library: {
    id: 'great_library',
    name: 'Great Library',
    icon: '📚',
    tier: 3,
    description: 'Repository of all human knowledge. Scholars gather from every nation.',
    flavorText: '"All the wisdom of the world, under one roof."',
    buildTime: 900,
    cost: {
      resources: { stone: 1500, gold: 800, knowledge: 300 },
      buildings: { library: 3 },
    },
    effects: {
      productionBonus: { knowledge: 0.55 },
      researchSpeedBonus: 0.18,
    },
    lore: 'Every ship that docks must surrender one book to the library\'s collection.',
  },

  colosseum: {
    id: 'colosseum',
    name: 'Grand Colosseum',
    icon: '🏟️',
    tier: 3,
    description: 'Massive arena for games and spectacles. The people demand entertainment!',
    flavorText: '"Bread and circuses keep the peace."',
    buildTime: 900,
    cost: {
      resources: { stone: 2500, gold: 600 },
      buildings: { quarry: 4 },
    },
    effects: {
      productionBonus: { culture: 0.50, gold: 0.45 },
      flatBonus: { population: 1.5 },
    },
    lore: 'Fifty thousand voices roar as champions clash in the arena.',
  },

  // ===========================================
  // TIER 4 - METALLURGY AGE (18 min build time)
  // ===========================================
  iron_colossus: {
    id: 'iron_colossus',
    name: 'Iron Colossus',
    icon: '🗽',
    tier: 4,
    description: 'A towering statue of iron and bronze. Visible from miles away.',
    flavorText: '"Let all who approach know our might."',
    buildTime: 1080,
    cost: {
      resources: { iron: 2000, stone: 1500, gold: 1000 },
      buildings: { mine: 4, forge: 2 },
    },
    effects: {
      productionBonus: { iron: 0.60, culture: 0.55 },
      globalProductionBonus: 0.10,
    },
    lore: 'Straddling the harbor entrance, ships pass beneath its mighty legs.',
  },

  great_forge: {
    id: 'great_forge',
    name: 'The Prime Forge',
    icon: '⚒️',
    tier: 4,
    description: 'Legendary forge where master smiths create wonders of metal.',
    flavorText: '"The hammer falls, and legends are born."',
    buildTime: 1080,
    cost: {
      resources: { iron: 2500, coal: 500, stone: 1000 },
      buildings: { forge: 4 },
    },
    effects: {
      productionBonus: { iron: 0.65, steel: 0.55 },
      flatBonus: { tools: 0.5 },
    },
    lore: 'Its furnaces never cool, fed by rivers of coal from the deep earth.',
  },

  underground_city: {
    id: 'golden_city',
    name: 'The Golden City',
    icon: '✨',
    tier: 4,
    description: 'A vast city carved into the mountain depths.',
    flavorText: '"A kingdom beneath the kingdom."',
    buildTime: 1080,
    cost: {
      resources: { stone: 3000, iron: 1500, gold: 500 },
      buildings: { mine: 5, quarry: 3 },
    },
    effects: {
      productionBonus: { stone: 0.60, iron: 0.60, coal: 0.50 },
      flatBonus: { population: 2 },
    },
    lore: 'Miles of tunnels connect chambers that could hold entire cities.',
  },

  // ===========================================
  // TIER 5 - KNOWLEDGE AGE (20 min build time)
  // ===========================================
  academy_of_wisdom: {
    id: 'academy_of_wisdom',
    name: 'Academy of Wisdom',
    icon: '🎓',
    tier: 5,
    description: 'The greatest minds gather to push the boundaries of knowledge.',
    flavorText: '"Question everything. Accept nothing."',
    buildTime: 1200,
    cost: {
      resources: { stone: 2000, gold: 2000, knowledge: 1000, culture: 500 },
      buildings: { library: 5, university: 2 },
    },
    effects: {
      productionBonus: { knowledge: 0.80, science: 0.70 },
      researchSpeedBonus: 0.30,
    },
    lore: 'Philosophers debate in marble halls while astronomers chart the heavens above.',
  },

  whisper_vault: {
    id: 'whisper_vault',
    name: 'Vault of Whispers',
    icon: '🏛️',
    tier: 5,
    description: 'Artifacts from every civilization, preserved for eternity.',
    flavorText: '"History preserved is wisdom gained."',
    buildTime: 1200,
    cost: {
      resources: { stone: 2500, gold: 1500, culture: 800, gems: 100 },
      buildings: { library: 3 },
    },
    effects: {
      productionBonus: { culture: 0.75, knowledge: 0.65 },
      flatBonus: { gold: 2 },
    },
    lore: 'From the first stone tools to the crown jewels of fallen empires.',
  },

  celestial_observatory: {
    id: 'celestial_observatory',
    name: 'Celestial Observatory',
    icon: '🔭',
    tier: 5,
    description: 'Peer into the cosmos and unlock the secrets of the stars.',
    flavorText: '"The answers lie written in the sky."',
    buildTime: 1200,
    cost: {
      resources: { stone: 1500, gold: 1000, knowledge: 800, gems: 150 },
      buildings: { university: 3 },
    },
    effects: {
      productionBonus: { science: 0.70, knowledge: 0.60 },
      researchSpeedBonus: 0.25,
      flatBonus: { science: 1 },
    },
    lore: 'The great lens reveals worlds beyond worlds, stars beyond counting.',
  },

  // ===========================================
  // TIER 6 - INDUSTRIAL AGE (25 min build time)
  // ===========================================
  grand_factory: {
    id: 'grand_factory',
    name: 'The Grand Manufactory',
    icon: '🏭',
    tier: 6,
    description: 'Automated production on a scale never before imagined.',
    flavorText: '"The machines never rest."',
    buildTime: 1500,
    cost: {
      resources: { iron: 5000, steel: 2000, coal: 2000, gold: 2000 },
      buildings: { factory: 5, forge: 3 },
    },
    effects: {
      productionBonus: { tools: 0.95, machinery: 0.85 },
      globalProductionBonus: 0.18,
    },
    lore: 'Steam and smoke rise day and night as a thousand machines thunder.',
  },

  great_railway: {
    id: 'great_railway',
    name: 'Transcontinental Railway',
    icon: '🚂',
    tier: 6,
    description: 'Iron roads connect every corner of the realm.',
    flavorText: '"Distance is no longer an obstacle."',
    buildTime: 1500,
    cost: {
      resources: { steel: 4000, iron: 3000, coal: 1500, gold: 1500 },
      buildings: { factory: 3 },
    },
    effects: {
      productionBonus: { gold: 0.90, coal: 0.80 },
      globalProductionBonus: 0.15,
      flatBonus: { population: 3 },
    },
    lore: 'A thousand miles of track, connecting cities that once took months to reach.',
  },

  eternal_dynamo: {
    id: 'eternal_dynamo',
    name: 'Eternal Dynamo',
    icon: '⚡',
    tier: 6,
    description: 'Harness the power of lightning itself.',
    flavorText: '"We have captured the storm."',
    buildTime: 1500,
    cost: {
      resources: { steel: 3000, machinery: 500, coal: 3000, gems: 200 },
      buildings: { factory: 4, steelMill: 2 },
    },
    effects: {
      productionBonus: { energy: 1.00, machinery: 0.85 },
      researchSpeedBonus: 0.30,
      flatBonus: { energy: 3 },
    },
    lore: 'Massive turbines spin eternally, feeding power to the hungry machines.',
  },

  // ===========================================
  // TIER 7 - COMMERCE AGE (28 min build time)
  // ===========================================
  world_bank: {
    id: 'world_bank',
    name: 'World Treasury',
    icon: '🏦',
    tier: 7,
    description: 'The financial heart of civilization. Gold flows like water.',
    flavorText: '"Gold is the blood of empires."',
    buildTime: 1680,
    cost: {
      resources: { gold: 10000, gems: 500, steel: 2000 },
      buildings: { bank: 5, market: 5 },
    },
    effects: {
      productionBonus: { gold: 1.20, gems: 1.00 },
      flatBonus: { gold: 10 },
    },
    lore: 'Vaults deeper than mountains hold the wealth of a hundred kingdoms.',
  },

  bap_hq: {
    id: 'bap_hq',
    name: 'BAPBAP HQ',
    icon: '🏢',
    tier: 7,
    description: 'A certain Ray and his band of cronies are hard at work developing YAPYAP.',
    flavorText: '"Were called Maison BAP now." - Bocatan.',
    buildTime: 1680,
    cost: {
      resources: { gold: 8000, culture: 1000, gems: 300 },
      buildings: { market: 6, tradingPost: 3 },  // FIXED: warehouse -> tradingPost
    },
    effects: {
      productionBonus: { gold: 1.10, culture: 1.05 },
      globalProductionBonus: 0.22,
    },
    lore: 'Winter sends his regards.',
  },

  diplomatic_palace: {
    id: 'diplomatic_palace',
    name: 'Palace of Nations',
    icon: '🏰',
    tier: 7,
    description: 'Where the fate of civilizations is decided through words, not war.',
    flavorText: '"The pen is mightier than the sword."',
    buildTime: 1680,
    cost: {
      resources: { stone: 5000, gold: 5000, culture: 2000, gems: 400 },
      buildings: { palace: 1, cathedral: 1 },  // FIXED: embassy -> palace + cathedral
    },
    effects: {
      productionBonus: { culture: 1.15, gold: 1.05 },
      researchSpeedBonus: 0.40,
      flatBonus: { population: 5, knowledge: 2 },
    },
    lore: 'Ambassadors from a hundred realms walk these gilded halls.',
  },

  // ===========================================
  // TIER 8 - ADVANCED AGE (32 min build time)
  // ===========================================
  research_complex: {
    id: 'research_complex',
    name: 'The Nexus Institute',
    icon: '🔬',
    tier: 8,
    description: 'Where science pushes beyond the boundaries of the possible.',
    flavorText: '"Today\'s magic is tomorrow\'s science."',
    buildTime: 1920,
    cost: {
      resources: { steel: 5000, science: 2000, energy: 1000, gems: 500 },
      buildings: { laboratory: 5, university: 4 },  // FIXED: lab -> laboratory
    },
    effects: {
      productionBonus: { science: 1.35, knowledge: 1.25 },
      researchSpeedBonus: 0.50,
    },
    lore: 'Theories once thought impossible become reality in these hallowed labs.',
  },

  automation_hub: {
    id: 'automation_hub',
    name: 'The Automaton Spire',
    icon: '🤖',
    tier: 8,
    description: 'Self-replicating machines that build more machines.',
    flavorText: '"We have created creators."',
    buildTime: 1920,
    cost: {
      resources: { steel: 6000, machinery: 2000, energy: 1500, science: 1000 },
      buildings: { factory: 6, laboratory: 3 },  // FIXED: lab -> laboratory
    },
    effects: {
      productionBonus: { machinery: 1.40, tools: 1.30 },
      globalProductionBonus: 0.28,
    },
    lore: 'Mechanical servants toil endlessly, never tiring, never stopping.',
  },

  space_elevator: {
    id: 'space_elevator',
    name: 'Stairway to Heaven',
    icon: '🚀',
    tier: 8,
    description: 'A ribbon of carbon stretching to the stars.',
    flavorText: '"We reach for the infinite."',
    buildTime: 1920,
    cost: {
      resources: { steel: 8000, science: 3000, energy: 2000, crystals: 100 },
      buildings: { laboratory: 4, factory: 5 },  // FIXED: lab -> laboratory
    },
    effects: {
      productionBonus: { science: 1.30, energy: 1.20 },
      researchSpeedBonus: 0.45,
      flatBonus: { science: 5, crystals: 0.5 },
    },
    lore: 'A thread connecting earth to the cosmos, humanity\'s first step to the stars.',
  },

  // ===========================================
  // TIER 9 - SCIENTIFIC AGE (36 min build time)
  // ===========================================
  quantum_computer: {
    id: 'quantum_computer',
    name: 'The Infinite Engine',
    icon: '💠',
    tier: 9,
    description: 'Computes solutions across infinite parallel realities.',
    flavorText: '"Every answer exists. We merely find them."',
    buildTime: 2160,
    cost: {
      resources: { science: 5000, energy: 4000, crystals: 500, gems: 1000 },
      buildings: { laboratory: 7, researchCenter: 3 },  // FIXED: lab -> laboratory, computer -> researchCenter
    },
    effects: {
      productionBonus: { science: 1.60, knowledge: 1.50 },
      researchSpeedBonus: 0.70,
    },
    lore: 'Processing power beyond comprehension, calculating the incalculable.',
  },

  fusion_reactor: {
    id: 'fusion_reactor',
    name: 'Stellar Heart',
    icon: '☀️',
    tier: 9,
    description: 'A miniature sun, providing unlimited clean energy.',
    flavorText: '"We have captured a star."',
    buildTime: 2160,
    cost: {
      resources: { steel: 10000, science: 4000, energy: 3000, crystals: 400 },
      buildings: { powerPlant: 5, laboratory: 5 },  // FIXED: power_station -> powerPlant, lab -> laboratory
    },
    effects: {
      productionBonus: { energy: 1.65 },
      globalProductionBonus: 0.38,
      flatBonus: { energy: 20 },
    },
    lore: 'The same fires that light the heavens now power our civilization.',
  },

  genetic_archive: {
    id: 'genetic_archive',
    name: 'The Lifecode Vault',
    icon: '🧬',
    tier: 9,
    description: 'The genetic blueprint of every species, preserved forever.',
    flavorText: '"Life itself, catalogued and preserved."',
    buildTime: 2160,
    cost: {
      resources: { science: 6000, knowledge: 3000, crystals: 600, gold: 5000 },
      buildings: { laboratory: 6, university: 5 },  // FIXED: lab -> laboratory
    },
    effects: {
      productionBonus: { science: 1.55, knowledge: 1.50 },
      researchSpeedBonus: 0.65,
      flatBonus: { population: 10 },
    },
    lore: 'From the smallest microbe to the mightiest beast, all are recorded here.',
  },

  // ===========================================
  // TIER 10 - MAGIC AGE (40 min build time)
  // ===========================================
  arcane_nexus: {
    id: 'arcane_nexus',
    name: 'The Arcane Nexus',
    icon: '🔮',
    tier: 10,
    description: 'Where ley lines converge, raw magic flows like rivers.',
    flavorText: '"Reality bends to our will."',
    buildTime: 2400,
    cost: {
      resources: { magic: 5000, crystals: 2000, artifacts: 500, gems: 2000 },
      buildings: { wizardTower: 6, enchanter: 5 },  // FIXED: mage_tower -> wizardTower
    },
    effects: {
      productionBonus: { magic: 1.80, crystals: 1.65 },
      globalProductionBonus: 0.42,
      flatBonus: { magic: 10, artifacts: 1 },
    },
    lore: 'The very fabric of reality warps around this wellspring of power.',
  },

  eternal_library: {
    id: 'eternal_library',
    name: 'Library of Eternity',
    icon: '📖',
    tier: 10,
    description: 'Contains every book ever written, and every book that will be.',
    flavorText: '"Past, present, future - all are written here."',
    buildTime: 2400,
    cost: {
      resources: { knowledge: 10000, magic: 3000, artifacts: 300, culture: 5000 },
      buildings: { library: 8, wizardTower: 4 },  // FIXED: mage_tower -> wizardTower
    },
    effects: {
      productionBonus: { knowledge: 1.90, magic: 1.70 },
      researchSpeedBonus: 0.85,
    },
    lore: 'Its shelves extend into dimensions beyond mortal comprehension.',
  },

  crystal_spire: {
    id: 'crystal_spire',
    name: 'The Crystal Spire',
    icon: '💎',
    tier: 10,
    description: 'A tower of pure crystal that amplifies magical energies.',
    flavorText: '"Beauty and power, crystallized."',
    buildTime: 2400,
    cost: {
      resources: { crystals: 3000, gems: 3000, magic: 4000, gold: 10000 },
      buildings: { enchanter: 6, sanctum: 2 },  // Added sanctum requirement for balance
    },
    effects: {
      productionBonus: { crystals: 1.85, gems: 1.75, magic: 1.70 },
      globalProductionBonus: 0.48,
    },
    lore: 'Light refracts through its facets, painting the sky in impossible colors.',
  },

  // ===========================================
  // TIER 11 - TRANSCENDENCE (45 min build time)
  // ===========================================
  reality_anchor: {
    id: 'reality_anchor',
    name: 'The Reality Anchor',
    icon: '⚓',
    tier: 11,
    description: 'Stabilizes the fabric of existence itself.',
    flavorText: '"We have mastered reality."',
    buildTime: 2700,
    cost: {
      resources: {
        magic: 10000, crystals: 5000, artifacts: 1000,
        science: 10000, energy: 10000
      },
      buildings: { wizardTower: 8, laboratory: 8 },  // FIXED: mage_tower -> wizardTower, lab -> laboratory
    },
    effects: {
      productionBonus: { magic: 2.00, science: 1.95 },
      globalProductionBonus: 0.55,
    },
    lore: 'Dimensions collapse and expand at our command.',
  },

  ascension_gate: {
    id: 'ascension_gate',
    name: 'Gate of Ascension',
    icon: '🌟',
    tier: 11,
    description: 'A portal to realms beyond mortal understanding.',
    flavorText: '"The final threshold awaits."',
    buildTime: 2700,
    cost: {
      resources: {
        artifacts: 2000, crystals: 8000, magic: 8000,
        knowledge: 15000, culture: 10000
      },
      buildings: { temple: 6, wizardTower: 6 },  // FIXED: mage_tower -> wizardTower
    },
    effects: {
      productionBonus: { artifacts: 2.10, magic: 1.90, knowledge: 1.85 },
      researchSpeedBonus: 1.10,
      flatBonus: { artifacts: 5 },
    },
    lore: 'Those who pass through are changed forever.',
  },

  cosmic_beacon: {
    id: 'cosmic_beacon',
    name: 'Cosmic Beacon',
    icon: '✨',
    tier: 11,
    description: 'Signals our presence to civilizations beyond the stars.',
    flavorText: '"We are here. We have arrived."',
    buildTime: 2700,
    cost: {
      resources: {
        energy: 20000, science: 15000, magic: 5000,
        crystals: 5000, artifacts: 1000
      },
      buildings: { laboratory: 10, wizardTower: 5 },  // FIXED: lab -> laboratory, mage_tower -> wizardTower
    },
    effects: {
      productionBonus: { energy: 2.05, science: 2.00 },
      globalProductionBonus: 0.65,
      researchSpeedBonus: 1.00,
      flatBonus: {
        science: 20, energy: 20, magic: 10, knowledge: 10
      },
    },
    lore: 'A light in the darkness, visible across galaxies.',
  },
};

// ===========================================
// BALANCE CONSTANTS
// ===========================================
export const WONDER_BALANCE = {
  maxTotal: 3,
  maxUnderConstruction: 1,
  buildTimeMultiplier: 1.0,
  minBuildTime: 300,
  consumeBuildings: true,
  canCancel: true,
  cancelRefundPercent: 0.5,
  baseSpeedMultiplier: 1.0,

  wonderEraNames: {
    1: 'Primitive Age',
    2: 'Stone Age',
    3: 'Agricultural Age',
    4: 'Metallurgy Age',
    5: 'Knowledge Age',
    6: 'Industrial Age',
    7: 'Commerce Age',
    8: 'Advanced Age',
    9: 'Scientific Age',
    10: 'Arcane Age',
    11: 'Transcendence',
  },

  eraIcons: {
    1: '🏕️',
    2: '🪨',
    3: '🌾',
    4: '⚒️',
    5: '📜',
    6: '⚙️',
    7: '💰',
    8: '🔧',
    9: '🔬',
    10: '✨',
    11: '🌟',
  },

  eraBonuses: {
    1: 'Survival basics unlocked',
    2: 'Stone tools & construction',
    3: '+10% Food production',
    4: 'Metal tools & weapons',
    5: '+15% Research speed',
    6: 'Automated production',
    7: '+20% Gold income',
    8: 'Advanced machinery',
    9: '+25% All research',
    10: 'Mystical powers',
    11: 'Transcendence achieved',
  },
};

// ===========================================
// HELPER FUNCTIONS
// ===========================================

export const getWondersByTier = (tier) => {
  return Object.values(WONDERS).filter(w => w.tier === tier);
};

export const getWonder = (wonderId) => {
  return WONDERS[wonderId] || null;
};

export const getWonderTiers = () => {
  const tiers = new Set(Object.values(WONDERS).map(w => w.tier));
  return Array.from(tiers).sort((a, b) => a - b);
};

export const canAffordWonder = (wonder, playerResources, buildingCounts) => {
  const hasResources = Object.entries(wonder.cost.resources).every(
    ([res, amt]) => (playerResources[res] || 0) >= amt
  );
  const hasBuildings = Object.entries(wonder.cost.buildings || {}).every(
    ([bld, amt]) => (buildingCounts[bld] || 0) >= amt
  );
  return hasResources && hasBuildings;
};

export const calculateWonderBuildTime = (baseBuildTime, speedMultiplier = 1.0) => {
  const actualTime = baseBuildTime / speedMultiplier;
  return Math.max(actualTime, WONDER_BALANCE.minBuildTime);
};

export const formatWonderTime = (seconds) => {
  if (seconds < 60) {
    return `${Math.ceil(seconds)}s`;
  } else if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.ceil(seconds % 60);
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
};

export const getEraName = (tier) => {
  return WONDER_BALANCE.wonderEraNames[tier] || `Era ${tier}`;
};

export default WONDERS;
